import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { customerTicketsApi, customerProductsApi } from '../lib/api';
import { MessageSquare, Plus, AlertCircle, Clock, CheckCircle, XCircle, X, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import Pagination from '../components/Pagination';
import { readPagination, type PageInfo } from '../lib/pagination';
import { useTranslation } from 'react-i18next';

interface Ticket {
  id: string;
  subject: string;
  status: string;
  priority: string;
  department: string;
  createdAt: string;
  updatedAt: string;
  lastReplyAt: string | null;
  lastReplyBy: string | null;
  product?: { id: string; domain: string; type: string };
}

interface Product {
  id: string;
  domain: string;
  type: string;
}

const statusConfig: Record<string, { icon: any; color: string; bg: string }> = {
  OPEN: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100' },
  AWAITING_REPLY: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  RESOLVED: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' },
  CLOSED: { icon: XCircle, color: 'text-gray-600', bg: 'bg-gray-100' },
};

const priorityConfig: Record<string, string> = {
  LOW: 'bg-gray-100 text-gray-700',
  MEDIUM: 'bg-blue-100 text-blue-700',
  HIGH: 'bg-orange-100 text-orange-700',
  URGENT: 'bg-red-100 text-red-700',
};

export default function TicketsPage() {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [showNewTicket, setShowNewTicket] = useState(false);

  // New ticket form
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newPriority, setNewPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'>('MEDIUM');
  const [newDepartment, setNewDepartment] = useState('support');
  const [newProductId, setNewProductId] = useState('');
  const [creating, setCreating] = useState(false);

  // A different filter is a different list; the page you were on does not
  // survive it.
  useEffect(() => setPage(1), [statusFilter]);

  useEffect(() => {
    loadData();
  }, [statusFilter, page]);

  async function loadData() {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      
      params.page = page;

      const [ticketsRes, productsRes] = await Promise.all([
        customerTicketsApi.list(params),
        customerProductsApi.list(),
      ]);
      
      setTickets((ticketsRes.data as any) || []);
      setPageInfo(readPagination(ticketsRes));
      setProducts((productsRes.data as any) || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateTicket(e: React.FormEvent) {
    e.preventDefault();
    
    setCreating(true);
    setError(null);

    try {
      await customerTicketsApi.create({
        subject: newSubject,
        message: newMessage,
        priority: newPriority,
        department: newDepartment,
        productId: newProductId || undefined,
      });
      
      // Reset form and reload
      setNewSubject('');
      setNewMessage('');
      setNewPriority('MEDIUM');
      setNewDepartment('support');
      setNewProductId('');
      setShowNewTicket(false);
      loadData();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function formatRelativeTime(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('tickets.title')}</h1>
          <p className="text-gray-600">{t('tickets.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowNewTicket(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium"
        >
          <Plus className="h-5 w-5" />
          {t('tickets.newTicket')}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        {['', 'OPEN', 'AWAITING_REPLY', 'RESOLVED', 'CLOSED'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              statusFilter === status
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            {status.replace('_', ' ') || 'All'}
          </button>
        ))}
      </div>

      {/* Tickets List */}
      {tickets.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">{t('tickets.none')}</h3>
          <p className="mt-2 text-gray-500">{t('tickets.noneYet')}</p>
          <button
            onClick={() => setShowNewTicket(true)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
          >
            <Plus className="h-5 w-5" />
            {t('tickets.firstTicket')}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
          {tickets.map((ticket) => {
            const config = (statusConfig[ticket.status as keyof typeof statusConfig] ?? statusConfig.OPEN)!;
            const StatusIcon = config.icon;

            return (
              <Link
                key={ticket.id}
                to={`/tickets/${ticket.id}`}
                className="block p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium text-gray-900 truncate">{ticket.subject}</h3>
                      <span
                        className={clsx(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                          config.bg,
                          config.color
                        )}
                      >
                        <StatusIcon className="h-3 w-3" />
                        {ticket.status.replace('_', ' ')}
                      </span>
                      <span
                        className={clsx(
                          'px-2 py-0.5 rounded-full text-xs font-medium',
                          priorityConfig[ticket.priority]
                        )}
                      >
                        {ticket.priority}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                      <span>{ticket.department}</span>
                      {ticket.product && (
                        <span>• {ticket.product.domain}</span>
                      )}
                      <span>• Created {formatRelativeTime(ticket.createdAt)}</span>
                      {ticket.lastReplyAt && (
                        <span>
                          • Last reply {formatRelativeTime(ticket.lastReplyAt)}
                          {ticket.lastReplyBy === 'STAFF' && ' by Staff'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* New Ticket Modal */}
      {showNewTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold">{t('tickets.newTicketTitle')}</h2>
              <button
                onClick={() => setShowNewTicket(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreateTicket} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tickets.subject')}
                </label>
                <input
                  type="text"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  required
                  placeholder={t('tickets.subjectHint')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('tickets.department')}
                  </label>
                  <select
                    value={newDepartment}
                    onChange={(e) => setNewDepartment(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="support">{t('tickets.deptGeneral')}</option>
                    <option value="billing">{t('tickets.deptBilling')}</option>
                    <option value="technical">{t('tickets.deptTechnical')}</option>
                    <option value="sales">{t('tickets.deptSales')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('tickets.priority')}
                  </label>
                  <select
                    value={newPriority}
                    onChange={(e) => setNewPriority(e.target.value as any)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">{t('tickets.priorityMedium')}</option>
                    <option value="HIGH">{t('tickets.priorityHigh')}</option>
                    <option value="URGENT">{t('tickets.priorityUrgent')}</option>
                  </select>
                </div>
              </div>

              {products.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('tickets.relatedProduct')}
                  </label>
                  <select
                    value={newProductId}
                    onChange={(e) => setNewProductId(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="">{t('tickets.none_option')}</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.domain} ({product.type})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tickets.message')}
                </label>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  required
                  rows={6}
                  placeholder={t('tickets.messageHint')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowNewTicket(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  {t('tickets.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className={clsx(
                    'inline-flex items-center gap-2 px-6 py-2 rounded-lg font-medium text-white transition-colors',
                    creating ? 'bg-gray-400' : 'bg-brand-600 hover:bg-brand-700'
                  )}
                >
                  {creating ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Plus className="h-5 w-5" />
                  )}
                  Create Ticket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pageInfo && <Pagination {...pageInfo} onChange={setPage} />}
    </div>
  );
}
